import { m } from "framer-motion";
import { useState, useEffect } from "react";
// @mui
import List from "@mui/material/List";
import Stack from "@mui/material/Stack";
import Badge from "@mui/material/Badge";
import Drawer from "@mui/material/Drawer";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
// hooks
import { useBoolean } from "src/hooks/use-boolean";
// api
import { useGetList, readAll } from "src/api/notification";
// components
import Iconify from "src/components/iconify";
import Scrollbar from "src/components/scrollbar";
import { varHover } from "src/components/animate";
import { useLocales } from "src/locales";
// firebase
import { messaging } from "src/utils/firebase";
import { saveMessagingDeviceToken } from "src/utils/messaging";
import { onMessage } from "firebase/messaging";
import * as serviceWorkerRegistration from "src/service-worker-registration";
// auth
import { useAuthContext } from "src/auth/hooks";
//
import NotificationItem from "./notification-item";

export default function NotificationsPopover() {
  const { user } = useAuthContext();

  const [totalUnRead, setTotalUnRead] = useState(0);

  useEffect(() => {
    // Bail when there is no user: on logout this effect re-runs with user === null and would mint a
    // fresh FCM token right after expireSession() revoked the old one, for a registration the
    // backend would reject anyway.
    if (!user?.id) return;

    serviceWorkerRegistration.register(registration => {
      saveMessagingDeviceToken(registration);
    });
  }, [user]);

  const messageListener = async () => {
    const msg = await messaging();
    onMessage(msg, message => {
      setTotalUnRead(totalUnRead + 1);
      // eslint-disable-next-line no-new
      new Notification(message.notification.title, {
        icon: message.notification.image,
        body: message.notification.body,
      });
    });
  };

  messageListener();

  const drawer = useBoolean();

  const { t } = useLocales();
  const { notifications, mutate } = useGetList(user?.id);

  const onClickNotifications = () => {
    setTotalUnRead(0);
    drawer.onTrue();
    mutate();
    readAll(user?.id);
  };

  useEffect(() => {
    setTotalUnRead(notifications.filter(n => !n.read).length);
  }, [notifications]);

  const renderHead = (
    <Stack
      direction="row"
      alignItems="center"
      sx={{ py: 2, pl: 2.5, pr: 1, minHeight: 68 }}
    >
      <Typography variant="h6">{t("viewGeneric", { label: t("notifications") })}</Typography>
    </Stack>
  );

  const renderList =
    notifications.length > 0 ? (
      <Scrollbar>
        <List disablePadding>
          {notifications?.map((notification, index) => (
            <NotificationItem
              key={index}
              notification={notification}
              mutate={mutate}
            />
          ))}
        </List>
      </Scrollbar>
    ) : (
      <Stack
        direction="row"
        alignItems="center"
        sx={{
          display: "flex",
          justifyContent: "center",
          py: 2,
          pl: 2.5,
          pr: 1,
          minHeight: 68,
          mt: 5,
        }}
      >
        <Typography variant="h7">{t("noNotifications")}</Typography>
      </Stack>
    );

  return (
    notifications != null && (
      <>
        <IconButton
          component={m.button}
          whileTap="tap"
          whileHover="hover"
          variants={varHover(1.05)}
          aria-label="notifications"
          color={drawer.value ? "primary" : "default"}
          onClick={onClickNotifications}
        >
          <Badge
            badgeContent={totalUnRead}
            color="error"
          >
            <Iconify
              icon="solar:bell-bing-bold-duotone"
              width={24}
            />
          </Badge>
        </IconButton>

        <Drawer
          open={drawer.value}
          onClose={drawer.onFalse}
          anchor="right"
          slotProps={{
            backdrop: { invisible: true },
          }}
          PaperProps={{
            sx: { width: 1, maxWidth: 420 },
          }}
        >
          {renderHead}

          <Divider />

          {renderList}
        </Drawer>
      </>
    )
  );
}
